<template>
    <!-- selection container -->
    <div class="max-tablet:flex-col max-tablet:gap-3 mb-5 flex flex-wrap gap-5">
        <div class="flex w-full flex-col gap-1">
            <div class="text-sm">{{ getStrByLng().LABEL_SELECT_DISCIPLINE }}</div>
            <div class="max-mobile-xl:flex-col flex gap-5">
                <Select
                    v-model="selectedDiscipline"
                    class="max-mobile:w-full"
                    :empty-message="getStrByLng().MESSAGE_NO_DISCIPLINES"
                    option-label="label"
                    option-value="value"
                    :options="disciplineSelectionList"
                    :placeholder="getStrByLng().LABEL_ALL_DISCIPLINES"
                />
            </div>
        </div>
    </div>
    <!-- content -->
    <div>
        <WdlArchiveFetch
            :is-data-available="diamondRaceData !== undefined"
            :is-fetching="isFetchingDiamondRaceData"
            :place-holder-msg="getStrByLng().MESSAGE_NO_STANDINGS"
        >
            <ul v-if="selectedDisciplineData" class="flex flex-col gap-5">
                <WdlArchiveStandingsPreviewList :discipline="selectedDisciplineData" />
            </ul>
        </WdlArchiveFetch>
    </div>
</template>

<script setup lang="ts">
import { useOwlStore } from "@swisstiming/owl-core";
import { getStrByLng } from "@swisstiming/webtec-kit";
import { AfterFetchContext, useFetch } from "@vueuse/core";
import Select from "primevue/select";
import { computed, ref } from "vue";

import WdlArchiveFetch from "../components/WdlArchiveFetch.vue";
import WdlArchiveStandingsPreviewList from "../components/WdlArchiveStandingsPreviewList.vue";
import { IDiamondRaceJson } from "../interfaces/wdl-archive.interfaces";
import { useArchiveStore } from "../stores/wdl-archive.store";

// removed the min-height (which is set to make sure the dropdowns throughout the archive´-pages fit into the iframe)
// here the min-height can be set to 0, because the height is enough because of the rendered content
document.body.className = "min-h-0";

const archiveStore = useArchiveStore();
const owlStore = useOwlStore();

const selectedSeason = ref<string>();
// if url-param is provided use param otherwise the default selected season (latest)
const relevantSeason = computed(() => (owlStore.qParams?.season ?? selectedSeason.value)?.toLowerCase?.());

const selectedDiscipline = ref<string>();

// diamond_race.json
const { data: diamondRaceData, isFetching: isFetchingDiamondRaceData } = useFetch(
    computed(() => archiveStore.getDiamondRaceJsonUrl(relevantSeason.value)),
    {
        refetch: true,
        updateDataOnError: true,
        afterFetch(ctx: AfterFetchContext<IDiamondRaceJson>) {
            const initialSelectedDiscipline = Object.values(ctx.data.Data.Women ?? {}).at(0);
            selectedDiscipline.value = initialSelectedDiscipline.Code;
            return ctx;
        },
        onFetchError(ctx) {
            // reset selections if requested data is not available
            selectedDiscipline.value = undefined;
            ctx.data = undefined;
            return ctx;
        }
    }
).json<IDiamondRaceJson>();

// seasons.json
useFetch(archiveStore.seasonsJsonUrl, {
    refetch: true,
    afterFetch(ctx: AfterFetchContext<string[]>) {
        // preselect latest season as default
        selectedSeason.value = ctx.data?.at(-1);

        return ctx;
    }
}).json<string[]>();

const womenDisciplineList = computed(() => Object.values(diamondRaceData.value?.Data?.Women ?? {}));
const menDisciplineList = computed(() => Object.values(diamondRaceData.value?.Data?.Men ?? {}));
const combinedDisciplinesList = computed(() =>
    Object.values([...womenDisciplineList.value, ...menDisciplineList.value])
);

const disciplineSelectionList = computed(() =>
    combinedDisciplinesList.value?.map((item) => {
        return {
            value: item.Code,
            label: item.Name
        };
    })
);

const selectedDisciplineData = computed(() =>
    combinedDisciplinesList.value.find((item) => item.Code === selectedDiscipline.value)
);
</script>
